// FILE 21: src/components/catalog/ProductModal.js
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import { PRODUCTS } from '../../data/products';
import './ProductModal.css';

/** Extract YouTube video ID from a URL, or return null. */
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

/** Detect Vimeo ID from a URL, or return null. */
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

const CATEGORY_LABELS = {
  marble: 'Marble',
  porcelain: 'Porcelain',
  ceramic: 'Ceramic',
  naturalStone: 'Natural Stone',
  woodLook: 'Wood Look',
  mosaic: 'Mosaic',
};

export default function ProductModal({ product: initialProduct, onClose }) {
  const [product, setProduct] = useState(initialProduct);
  useEffect(() => { setProduct(initialProduct); }, [initialProduct?.id]); // eslint-disable-line
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' | 'video'
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Reset state when product changes
  useEffect(() => {
    setActiveIdx(0);
    setSelectedSize(null);
    setActiveTab('photos');
    setLightboxOpen(false);
  }, [product?.id]);

  // Keyboard close
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!product) return;

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [product, handleKeyDown]);

  if (!product) return null;

  const {
    name,
    category,
    collection,
    finish,
    sizes,
    priceRange,
    description,
    images,
    videoUrl,
    patternConfig,
  } = product;

  const fallbackBg = patternConfig?.baseColor || '#c0bbb4';
  const currentImage = images && images[activeIdx] ? images[activeIdx] : null;

  // Contact link with tile pre-fill
  const contactLink = `/contact?tile=${encodeURIComponent(name)}`;

  // Related products — same category, exclude self, up to 4
  const related = PRODUCTS
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  // Video detection
  const ytId     = getYouTubeId(videoUrl);
  const vimeoId  = getVimeoId(videoUrl);
  const isDirectVideo = videoUrl && !ytId && !vimeoId;

  return (
    <>
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`${name} product details`}
    >
      <div className="modal">
        {/* Close button */}
        <button className="modal__close" onClick={onClose} aria-label="Close">
          &#x2715;
        </button>

        {/* ---- Left: Gallery ---- */}
        <div className="modal__gallery">

          {/* Tab switcher */}
          <div className="modal__tabs">
            <button
              className={`modal__tab${activeTab === 'photos' ? ' modal__tab--active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              Photos
            </button>
            <button
              className={`modal__tab${activeTab === 'video' ? ' modal__tab--active' : ''}`}
              onClick={() => setActiveTab('video')}
            >
              ▶&nbsp; Video
            </button>
          </div>

          {/* ── Photos panel ── */}
          {activeTab === 'photos' && (
            <>
              {currentImage ? (
                <picture onClick={() => setLightboxOpen(true)} style={{ cursor: 'zoom-in', display: 'block' }}>
                  <source srcSet={currentImage.url.replace(/\.(jpg|jpeg|png)$/i, '.webp')} type="image/webp" />
                  <img
                    className="modal__main-image"
                    src={currentImage.url}
                    alt={currentImage.alt || name}
                  />
                </picture>
              ) : (
                <div
                  className="modal__main-image-fallback"
                  style={{ background: fallbackBg }}
                >
                  {name}
                </div>
              )}

              {images && images.length > 1 && (
                <div className="modal__thumbs">
                  {images.map((img, idx) => (
                    <img
                      key={idx}
                      className={`modal__thumb${activeIdx === idx ? ' modal__thumb--active' : ''}`}
                      src={img.url}
                      alt={img.alt || `View ${idx + 1}`}
                      onClick={() => setActiveIdx(idx)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Video panel ── */}
          {activeTab === 'video' && (
            <div className="modal__video-panel">
              {ytId ? (
                <iframe
                  className="modal__video-embed"
                  src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                  title={`${name} video`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : vimeoId ? (
                <iframe
                  className="modal__video-embed"
                  src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
                  title={`${name} video`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : isDirectVideo ? (
                <video
                  className="modal__video-embed"
                  src={videoUrl}
                  controls
                  autoPlay
                />
              ) : (
                /* ── No video yet placeholder ── */
                <div className="modal__video-placeholder">
                  <div className="modal__video-placeholder__icon">🎬</div>
                  <p className="modal__video-placeholder__title">
                    Video Coming Soon
                  </p>
                  <p className="modal__video-placeholder__sub">
                    A product walkthrough video for <strong>{name}</strong> is
                    being prepared. Check back soon or contact us for a live
                    consultation.
                  </p>
                  <Link
                    to={contactLink}
                    onClick={onClose}
                    className="modal__video-placeholder__link"
                  >
                    Request a Consultation →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- Right: Info ---- */}
        <div className="modal__info">
          {collection && (
            <div className="modal__collection">{collection.charAt(0).toUpperCase() + collection.slice(1)} Collection</div>
          )}
          <div className="modal__category">{CATEGORY_LABELS[category] || category}</div>
          <h2 className="modal__name">{name}</h2>
          <p className="modal__price">Contact for pricing</p>

          <p className="modal__description">{description}</p>

          {/* Sizes */}
          {sizes && sizes.length > 0 && (
            <div>
              <div className="modal__sizes-label">Available Sizes</div>
              <div className="modal__sizes">
                {sizes.map((s) => (
                  <button
                    key={s}
                    className={`modal__size-chip${selectedSize === s ? ' modal__size-chip--selected' : ''}`}
                    onClick={() => setSelectedSize(selectedSize === s ? null : s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Spec table */}
          <table className="modal__specs" aria-label="Product specifications">
            <tbody>
              {collection && (
                <tr>
                  <td>Collection</td>
                  <td>{collection.charAt(0).toUpperCase() + collection.slice(1)}</td>
                </tr>
              )}
              <tr>
                <td>Material</td>
                <td>{CATEGORY_LABELS[category] || category}</td>
              </tr>
              <tr>
                <td>Finish</td>
                <td>{finish}</td>
              </tr>
              <tr>
                <td>Available Sizes</td>
                <td>{sizes ? sizes.join(', ') : '—'}</td>
              </tr>
              <tr>
                <td>PEI Rating</td>
                <td>3–4 (varies by size)</td>
              </tr>
              <tr>
                <td>In Stock</td>
                <td>{product.inStock ? 'Yes' : 'Call for Availability'}</td>
              </tr>
            </tbody>
          </table>

          {/* CTA */}
          <div className="modal__cta">
            <Link to={contactLink} onClick={onClose}>
              <Button variant="secondary" size="md" style={{ width: '100%' }}>
                Contact Us
              </Button>
            </Link>
            <Link to={`/contact?tile=${encodeURIComponent(name)}&type=sample`} onClick={onClose}>
              <Button variant="primary" size="md" style={{ width: '100%' }}>
                Request a Sample
              </Button>
            </Link>
          </div>
        </div>

        {/* ── Complete the Look ── */}
        {related.length > 0 && (
          <div className="modal__related">
            <h4 className="modal__related-heading">Complete the Look</h4>
            <div className="modal__related-grid">
              {related.map(p => (
                <button
                  key={p.id}
                  className="modal__related-card"
                  onClick={() => { setProduct(p); setActiveIdx(0); setLightboxOpen(false); }}
                >
                  {p.images?.[0] ? (
                    <picture>
                      <source srcSet={p.images[0].url.replace(/\.(jpg|jpeg|png)$/i, '.webp')} type="image/webp" />
                      <img src={p.images[0].url} alt={p.name} className="modal__related-img" />
                    </picture>
                  ) : (
                    <div className="modal__related-fallback" style={{ background: p.patternConfig?.baseColor }} />
                  )}
                  <span className="modal__related-name">{p.name}</span>
                  <span className="modal__related-size">{p.sizes?.[0]}"</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

      {/* ── Lightbox ── */}
      {lightboxOpen && currentImage && (
        <div className="lightbox" onClick={() => setLightboxOpen(false)}>
          <button className="lightbox__close" onClick={() => setLightboxOpen(false)} aria-label="Close">&#x2715;</button>
          {activeIdx > 0 && (
            <button className="lightbox__prev" onClick={(e) => { e.stopPropagation(); setActiveIdx(i => i - 1); }} aria-label="Previous">&#8249;</button>
          )}
          <picture onClick={e => e.stopPropagation()}>
            <source srcSet={currentImage.url.replace(/\.(jpg|jpeg|png)$/i, '.webp')} type="image/webp" />
            <img className="lightbox__img" src={currentImage.url} alt={currentImage.alt || name} />
          </picture>
          {images && activeIdx < images.length - 1 && (
            <button className="lightbox__next" onClick={(e) => { e.stopPropagation(); setActiveIdx(i => i + 1); }} aria-label="Next">&#8250;</button>
          )}
          <p className="lightbox__counter">{activeIdx + 1} / {images?.length}</p>
        </div>
      )}
    </>
  );
}
